import {Quality} from "@/store/player/types";
import PlayerStore from "@/store/player";

export default class QualityUtils {

    /**
     * Автоопределение стоит денег (performance.memory + try/catch), а getQuality() дёргается
     * очень часто: из CU.windowWidth(), из SpineUtils.resolveConfig() на КАЖДОЕ создание спрайта.
     * Железо за сессию не меняется, поэтому автоопределение считаем один раз.
     * Явный выбор игрока в настройках читается каждый раз и кэш не трогает.
     */
    private static autoQuality: Quality | undefined;

    /**
     * Автоопределение по памяти не видит слабый GPU/CPU: старый десктоп с Chromium получает
     * HIGH, потому что лимит кучи там всегда большой. Если RoadScene по фактическому FPS
     * решила, что устройство не тянет (вторая ступень адаптивной деградации), опускаем
     * АВТО-качество до LOW и запоминаем это в localStorage, чтобы следующая сессия
     * стартовала уже лёгкой, а не разгонялась до просадки заново.
     * Явный выбор качества игроком в настройках этот механизм не трогает.
     */
    private static readonly RUNTIME_DOWNGRADE_KEY = 'perfQualityCap';
    private static runtimeDowngraded: boolean | undefined;
    private static pendingCanvasResize: boolean = false;

    public static isRuntimeDowngraded(): boolean {
        if (QualityUtils.runtimeDowngraded === undefined) {
            try {
                QualityUtils.runtimeDowngraded = window.localStorage.getItem(QualityUtils.RUNTIME_DOWNGRADE_KEY) === '1';
            } catch (e) {
                QualityUtils.runtimeDowngraded = false;
            }
        }
        return QualityUtils.runtimeDowngraded;
    }

    /** Можно ли ещё понижать: только авто-режим, ещё не пониженный и не низкий сам по себе. */
    public static canRuntimeDowngrade(): boolean {
        let quality = PlayerStore.getQuality();
        if (quality && quality !== Quality.AUTO) {
            return false;
        }
        return !QualityUtils.isRuntimeDowngraded() && !QualityUtils.isLowQuality();
    }

    public static applyRuntimeDowngrade(): void {
        QualityUtils.runtimeDowngraded = true;
        QualityUtils.pendingCanvasResize = true;
        try {
            window.localStorage.setItem(QualityUtils.RUNTIME_DOWNGRADE_KEY, '1');
        } catch (e) {
            // localStorage недоступен - даунгрейд подействует хотя бы на текущую сессию
        }
    }

    /** Понижение качества меняет размер канваса; применяется на входе в RoadScene. */
    public static consumePendingCanvasResize(): boolean {
        const pending = QualityUtils.pendingCanvasResize;
        QualityUtils.pendingCanvasResize = false;
        return pending;
    }

    public static hasPendingCanvasResize(): boolean {
        return QualityUtils.pendingCanvasResize;
    }

    public static getReccommendedWidth() {
        let quality = QualityUtils.getQuality();

        switch (quality) {
            case Quality.LOWEST:
                return 450;
            case Quality.LOW:
                return 500;
            case Quality.AUTO:
            case Quality.STANDARD:
                return 600;
            case Quality.HIGH:
                return 768;
        }
    }

    /** LOW и LOWEST - режимы для слабых устройств, скелетные анимации в них отключены. */
    public static isLowQuality(): boolean {
        let quality = QualityUtils.getQuality();
        return quality === Quality.LOW || quality === Quality.LOWEST;
    }

    public static getQuality(): Quality {

        let quality = PlayerStore.getQuality();

        if (quality && quality !== Quality.AUTO) {
            return quality;
        }

        if (QualityUtils.autoQuality === undefined) {
            QualityUtils.autoQuality = QualityUtils.detectQuality();
        }

        if (QualityUtils.isRuntimeDowngraded()
            && (QualityUtils.autoQuality === Quality.HIGH || QualityUtils.autoQuality === Quality.STANDARD)) {
            return Quality.LOW;
        }

        return QualityUtils.autoQuality;
    }

    private static detectQuality(): Quality {

        // performance.memory есть только в Chromium. Там это самый точный сигнал: лимит кучи
        // напрямую зависит от объёма RAM устройства.
        try {
            // @ts-ignore
            let availableMemory = performance.memory.jsHeapSizeLimit / 1000000; // will give you the JS heap size

            if (availableMemory > 1100) { // 1130 = 6GB RAM
                return Quality.HIGH;
            } else if (availableMemory > 600) { // 793 = 3GB RAM
                return Quality.STANDARD;
            } else if (availableMemory > 450) {  // 491 = 2GB RAM
                return Quality.LOW;
            } else {
                return Quality.LOWEST;
            }

        } catch (e) {
            // do nothing
        }

        // Вне Chromium (iOS Safari, Firefox, часть webview) performance.memory нет, и раньше мы
        // отсюда уходили в HIGH - то есть слабое устройство получало канвас 768px и все шейдеры.
        // deviceMemory отдаёт объём RAM в гигабайтах и округлён вниз до степени двойки.
        try {
            // @ts-ignore
            const deviceMemory: number | undefined = navigator.deviceMemory;
            if (typeof deviceMemory === 'number' && deviceMemory > 0) {
                if (deviceMemory <= 1) {
                    return Quality.LOWEST;
                } else if (deviceMemory <= 2) {
                    return Quality.LOW;
                } else if (deviceMemory <= 4) {
                    return Quality.STANDARD;
                }
                return Quality.HIGH;
            }
        } catch (e) {
            // do nothing
        }

        // Сигналов нет вообще. Не занижаем: реальную просадку добьёт адаптивная деградация
        // эффектов в RoadScene, она смотрит на фактический FPS, а не на догадки о железе.
        return Quality.HIGH;
    }
}
